/**
 * ProducerSetupVisibilityCard — compact status module for onboarding footer.
 * Shows connected place, producer publish status, and a single clear CTA.
 */

import { ArrowRight, Eye, Circle, MapPin, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

type Status = 'draft' | 'public';

interface Props {
  placeStatus: Status | string | null;
  producerStatus: Status | string | null;
  placeName?: string | null;
  placeSlug?: string | null;
  placeMunicipality?: string | null;
  producerSlug?: string | null;
  hasPlace?: boolean;
  onPublishProducer: () => void;
  publishPending?: boolean;
}

function isPublished(s: string | null | undefined): boolean {
  return s === 'public';
}

function StatusBadge({ published }: { published: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${
        published
          ? 'bg-primary/10 text-primary'
          : 'bg-muted text-muted-foreground'
      }`}
    >
      <Circle
        className={`h-2 w-2 ${published ? 'fill-primary text-primary' : 'fill-muted-foreground/40 text-muted-foreground/40'}`}
      />
      {published ? 'Publicerad' : 'Utkast'}
    </span>
  );
}

export function ProducerSetupVisibilityCard({
  placeStatus,
  producerStatus,
  placeName,
  placeSlug,
  placeMunicipality,
  producerSlug,
  hasPlace = true,
  onPublishProducer,
  publishPending,
}: Props) {
  const placePublished = isPublished(placeStatus);
  const producerPublished = isPublished(producerStatus);
  const missingPlace = !hasPlace && !placeName;

  return (
    <div className="rounded-lg border border-border/60 bg-card p-5">
      {/* Header */}
      <h3 className="text-sm font-semibold text-foreground mb-0.5">Synlighet</h3>
      <p className="text-xs text-muted-foreground mb-4">
        Du kan fortsätta i din takt. Inget publiceras förrän du väljer det.
      </p>

      {/* Connected place */}
      {placeName && (
        <div className="flex items-start gap-2 mb-4 p-3 rounded-md bg-muted/50">
          <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground truncate">
              {placeName}
              {placeMunicipality && (
                <span className="text-muted-foreground font-normal">, {placeMunicipality}</span>
              )}
            </p>
            {placeSlug && (
              <a
                href={`/place/${placeSlug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-0.5"
              >
                Visa plats
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          <StatusBadge published={placePublished} />
        </div>
      )}

      {/* Status rows */}
      <div className="space-y-2 mb-4">
        {!placeName && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">Plats</span>
            <StatusBadge published={placePublished} />
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-sm text-foreground">Värdprofil</span>
          <StatusBadge published={producerPublished} />
        </div>
      </div>

      {/* CTA logic */}
      {missingPlace ? (
        <div>
          <p className="text-xs text-destructive mb-2">
            Ingen plats är kopplad till din värdprofil. Kontakta Farmly-teamet.
          </p>
          <Button size="sm" disabled>
            Publicera värdprofil
          </Button>
        </div>
      ) : producerPublished && placePublished ? (
        <div>
          <p className="text-sm text-primary font-medium mb-2">Du syns på Farmly.</p>
          {producerSlug && (
            <Button variant="outline" size="sm" asChild>
              <a href={`/producer/${producerSlug}?preview=1`} target="_blank" rel="noopener noreferrer">
                <Eye className="h-3.5 w-3.5 mr-1.5" />
                Förhandsvisa
              </a>
            </Button>
          )}
        </div>
      ) : producerPublished && !placePublished ? (
        <div>
          <p className="text-xs text-amber-700 dark:text-amber-400 mb-2">
            Värdprofilen är publicerad, men platsen är fortfarande utkast och syns inte.
          </p>
          <p className="text-xs text-muted-foreground">
            Kontakta Farmly-teamet för att publicera platsen.
          </p>
        </div>
      ) : !placePublished ? (
        <div>
          <p className="text-xs text-muted-foreground mb-2">
            Platsen behöver vara publicerad för att kunna synas.
          </p>
          <p className="text-xs text-muted-foreground/60">
            Kontakta Farmly-teamet när du känner dig redo.
          </p>
        </div>
      ) : (
        /* Place public, producer draft — can publish */
        <div>
          <p className="text-xs text-muted-foreground mb-2">
            När du publicerar din värdprofil:
          </p>
          <ul className="text-xs text-muted-foreground space-y-1 mb-3 ml-1">
            <li className="flex items-start gap-1.5">
              <span className="text-primary mt-0.5">•</span>
              Visas du som värd på platsen
            </li>
            <li className="flex items-start gap-1.5">
              <span className="text-primary mt-0.5">•</span>
              Kan du ta emot bokningar
            </li>
            <li className="flex items-start gap-1.5">
              <span className="text-primary mt-0.5">•</span>
              Blir din kontaktinformation synlig
            </li>
          </ul>
          <Button onClick={onPublishProducer} disabled={publishPending} size="sm">
            {publishPending ? 'Publicerar…' : 'Publicera värdprofil'}
          </Button>
        </div>
      )}
    </div>
  );
}
